import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUnconfirmed, NudgeButton } from "@/components/staff/AttendanceNudge";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { SendIntakeForm } from "@/components/staff/SendIntakeForm";
import {
  CalendarClock, Search, Stethoscope, Loader2, Video, ExternalLink,
  CheckCircle2, Clock, XCircle, AlertTriangle, ClipboardList, Plus,
  BarChart3, UserCheck, FileText
} from "lucide-react";

const ConsultationsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("upcoming");
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [activeStatFilter, setActiveStatFilter] = useState(searchParams.get("filter") === "unconfirmed" ? "unconfirmed" : null);
  const [unconfirmed, reloadUnconfirmed] = useUnconfirmed(token);
  const unconfirmedById = Object.fromEntries(unconfirmed.map(u => [u.booking_id, u]));

  // Upcoming bookings
  const [upcoming, setUpcoming] = useState([]);
  const [upcomingTotal, setUpcomingTotal] = useState(0);
  const [bookingStats, setBookingStats] = useState(null);

  // Consultation notes
  const [notes, setNotes] = useState([]);
  const [notesTotal, setNotesTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Calendar status
  const [calendarStatus, setCalendarStatus] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAll(); }, [statusFilter, typeFilter]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [uRes, nRes, sRes, cRes] = await Promise.all([
        fetch(`${API}/staff/upcoming-consultations?include_past=true&limit=100`, { headers }),
        fetchNotes(),
        fetch(`${API}/staff/booking-stats`, { headers }),
        fetch(`${API}/staff/calendar/status`, { headers }),
      ]);
      if (uRes.ok) { const d = await uRes.json(); setUpcoming(d.consultations || []); setUpcomingTotal(d.total || 0); }
      if (sRes.ok) setBookingStats(await sRes.json());
      if (cRes.ok) setCalendarStatus(await cRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchNotes = async () => {
    let url = `${API}/staff/all-consultations?limit=50`;
    if (statusFilter !== "all") url += `&status=${statusFilter}`;
    if (typeFilter !== "all") url += `&consultation_type=${typeFilter}`;
    const res = await fetch(url, { headers: { Authorization: token ? `Bearer ${token}` : "" } });
    if (res.ok) { const d = await res.json(); setNotes(d.consultations || []); setNotesTotal(d.total || 0); }
    return res;
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      const res = await fetch(`${API}/staff/bookings/${bookingId}/status`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) { toast.success(`Booking marked as ${newStatus.replace("_", " ")}`); fetchAll(); reloadUnconfirmed(); }
      else toast.error("Failed to update status");
    } catch { toast.error("Connection error"); }
  };

  const filteredNotes = search
    ? notes.filter(n => n.patient_name?.toLowerCase().includes(search.toLowerCase()) || n.staff_name?.toLowerCase().includes(search.toLowerCase()))
    : notes;

  const upcomingFiltered = (() => {
    let list = search
      ? upcoming.filter(b => (b.patient_name || "").toLowerCase().includes(search.toLowerCase()))
      : upcoming;
    if (activeStatFilter === "upcoming") list = list.filter(b => !b.is_past && b.status === "confirmed");
    else if (activeStatFilter === "today") {
      const today = new Date().toISOString().slice(0, 10);
      list = list.filter(b => b.appointment_datetime?.startsWith(today));
    }
    else if (activeStatFilter === "completed") list = list.filter(b => b.status === "completed");
    else if (activeStatFilter === "no_show") list = list.filter(b => b.status === "no_show");
    else if (activeStatFilter === "with_meet") list = list.filter(b => b.meeting_url);
    else if (activeStatFilter === "unconfirmed") list = unconfirmed.map(u => upcoming.find(b => b.booking_id === u.booking_id) || { ...u, status: "confirmed", is_past: false });
    return list;
  })();

  const upcomingFuture = upcomingFiltered.filter(b => !b.is_past);
  const upcomingPast = upcomingFiltered.filter(b => b.is_past);

  const statusColor = {
    draft: "bg-yellow-50 text-yellow-700 border-yellow-200",
    reviewed: "bg-blue-50 text-blue-700 border-blue-200",
    approved: "bg-green-50 text-green-700 border-green-200",
  };

  const bookingStatusBadge = (s) => {
    const map = {
      confirmed: { cls: "bg-blue-50 text-blue-700 border-blue-200", icon: CheckCircle2 },
      completed: { cls: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle2 },
      cancelled: { cls: "bg-stone-100 text-stone-500 border-stone-200", icon: XCircle },
      no_show: { cls: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
      rescheduled: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
    };
    const cfg = map[s] || map.confirmed;
    const Icon = cfg.icon;
    return <Badge className={`${cfg.cls} text-xs`}><Icon className="w-3 h-3 mr-1" />{s?.replace("_", " ")}</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

  return (
    <div className="space-y-6" data-testid="consultations-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">Consultations</h1>
          <p className="mt-1 text-base text-stone-500">Appointments, meetings, and consultation notes.</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {calendarStatus && (
            <Badge className={`text-xs ${calendarStatus.system_calendar_available ? "bg-green-50 text-green-700 border-green-200" : "bg-stone-100 text-stone-500"}`}>
              <Video className="w-3 h-3 mr-1" />
              {calendarStatus.system_calendar_available ? `Meet via ${calendarStatus.system_calendar_email}` : "Google Meet not connected"}
            </Badge>
          )}
        </div>
      </div>

      {/* Stats */}
      {bookingStats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="booking-stats">
          <MiniStat label="Total Bookings" value={bookingStats.total} icon={CalendarClock} onClick={() => { setActiveStatFilter(activeStatFilter === "all" ? null : "all"); setTab("upcoming"); }} active={activeStatFilter === "all"} />
          <MiniStat label="Upcoming" value={bookingStats.upcoming} icon={Clock} color="text-blue-600" onClick={() => { setActiveStatFilter(activeStatFilter === "upcoming" ? null : "upcoming"); setTab("upcoming"); }} active={activeStatFilter === "upcoming"} />
          <MiniStat label="Today" value={bookingStats.today} icon={CalendarClock} color="text-[#FF7A00]" onClick={() => { setActiveStatFilter(activeStatFilter === "today" ? null : "today"); setTab("upcoming"); }} active={activeStatFilter === "today"} />
          <MiniStat label="Completed" value={bookingStats.completed} icon={CheckCircle2} color="text-green-600" onClick={() => { setActiveStatFilter(activeStatFilter === "completed" ? null : "completed"); setTab("upcoming"); }} active={activeStatFilter === "completed"} />
          <MiniStat label="No-shows" value={bookingStats.no_show} icon={AlertTriangle} color="text-red-600" onClick={() => { setActiveStatFilter(activeStatFilter === "no_show" ? null : "no_show"); setTab("upcoming"); }} active={activeStatFilter === "no_show"} />
          <MiniStat label="Unconfirmed (24h)" value={unconfirmed.length} icon={UserCheck} color="text-amber-600" testid="stat-unconfirmed" onClick={() => { setActiveStatFilter(activeStatFilter === "unconfirmed" ? null : "unconfirmed"); setTab("upcoming"); }} active={activeStatFilter === "unconfirmed"} />
          <MiniStat label="With Meet Link" value={bookingStats.with_meeting_link} icon={Video} color="text-blue-600" onClick={() => { setActiveStatFilter(activeStatFilter === "with_meet" ? null : "with_meet"); setTab("upcoming"); }} active={activeStatFilter === "with_meet"} />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-stone-100 rounded-lg">
          <TabsTrigger value="upcoming" className="rounded-md text-sm" data-testid="tab-upcoming">
            Appointments ({upcomingTotal})
          </TabsTrigger>
          <TabsTrigger value="notes" className="rounded-md text-sm" data-testid="tab-notes">
            Notes ({notesTotal})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input
          placeholder={tab === "upcoming" ? "Search by patient..." : "Search by patient or staff..."}
          value={search} onChange={e => setSearch(e.target.value)}
          className="pl-10 rounded-lg" data-testid="search-input"
        />
      </div>

      {/* TAB: Upcoming Appointments */}
      {tab === "upcoming" && (
        <div className="space-y-4">
          {/* Future appointments */}
          {upcomingFuture.length > 0 && (
            <>
              <h3 className="text-sm font-bold text-stone-500 uppercase tracking-wider">Upcoming</h3>
              <div className="space-y-2">
                {upcomingFuture.map(bk => (
                  <BookingRow key={bk.booking_id} bk={bk} navigate={navigate} statusBadge={bookingStatusBadge} onStatusUpdate={handleStatusUpdate} nudge={unconfirmedById[bk.booking_id]} token={token} onNudged={reloadUnconfirmed} />
                ))}
              </div>
            </>
          )}

          {/* Past appointments */}
          {upcomingPast.length > 0 && (
            <>
              <h3 className="text-sm font-bold text-stone-400 uppercase tracking-wider mt-6">Past</h3>
              <div className="space-y-2 opacity-80">
                {upcomingPast.map(bk => (
                  <BookingRow key={bk.booking_id} bk={bk} navigate={navigate} statusBadge={bookingStatusBadge} onStatusUpdate={handleStatusUpdate} nudge={unconfirmedById[bk.booking_id]} token={token} onNudged={reloadUnconfirmed} />
                ))}
              </div>
            </>
          )}

          {upcoming.length === 0 && (
            <Card className="rounded-xl border-stone-200/60 p-12 text-center">
              <CalendarClock className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <p className="text-sm text-stone-500">No appointments found.</p>
            </Card>
          )}
        </div>
      )}

      {/* TAB: Consultation Notes */}
      {tab === "notes" && (
        <div className="space-y-3">
          <div className="flex gap-3 flex-wrap">
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); }}>
              <SelectTrigger className="w-40 rounded-lg"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); }}>
              <SelectTrigger className="w-44 rounded-lg"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="initial">Initial</SelectItem>
                <SelectItem value="follow_up">Follow-up</SelectItem>
                <SelectItem value="discharge">Discharge</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filteredNotes.length > 0 ? (
            <Card className="rounded-xl border-stone-200/60 overflow-hidden">
              <div className="hidden sm:grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr] gap-4 px-5 py-3 bg-stone-50 text-xs font-semibold text-stone-500 uppercase tracking-wider border-b border-stone-100">
                <span>Patient</span><span>Staff</span><span>Type</span><span>Date</span><span>Status</span>
              </div>
              <div className="divide-y divide-stone-100">
                {filteredNotes.map(n => (
                  <div key={n.note_id} className="px-5 py-3.5 sm:grid sm:grid-cols-[2fr_1.5fr_1fr_1fr_1fr] sm:gap-4 sm:items-center cursor-pointer hover:bg-stone-50/50 transition-colors" onClick={() => navigate(`/admin/patients/${n.patient_id}`)} data-testid={`note-row-${n.note_id}`}>
                    <div>
                      <p className="text-sm font-medium text-stone-900">{n.patient_name}</p>
                      <p className="text-xs text-stone-400 sm:hidden">By {n.staff_name}</p>
                    </div>
                    <p className="text-sm text-stone-600 hidden sm:block">{n.staff_name}</p>
                    <p className="text-sm text-stone-600 capitalize hidden sm:block">{n.consultation_type?.replace(/_/g, " ")}</p>
                    <p className="text-sm text-stone-500 hidden sm:block">{n.consultation_date?.split("T")[0]}</p>
                    <Badge variant="outline" className={`text-xs capitalize ${statusColor[n.status] || ""}`}>{n.status}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          ) : (
            <Card className="rounded-xl border-stone-200/60 p-12 text-center">
              <Stethoscope className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <p className="text-sm text-stone-500">No consultation notes found.</p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

const BookingRow = ({ bk, navigate, statusBadge, onStatusUpdate, nudge, token, onNudged }) => {
  const [showActions, setShowActions] = useState(false);

  const formatDate = (dt) => {
    try {
      const d = new Date(dt);
      return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Africa/Lagos" });
    } catch { return dt?.split("T")[0] || ""; }
  };

  const formatTime = (dt) => {
    try {
      const d = new Date(dt);
      return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Africa/Lagos" }) + " WAT";
    } catch { return ""; }
  };

  return (
    <Card className={`rounded-xl border-stone-200/60 p-4 ${bk.is_today ? "border-l-4 border-l-[#FF7A00]" : ""}`} data-testid={`booking-${bk.booking_id}`}>
      <div className="flex items-start gap-4">
        {/* Date/Time block */}
        <div className="hidden sm:flex flex-col items-center justify-center w-16 shrink-0 text-center">
          <span className="text-xs text-stone-400">{formatDate(bk.appointment_datetime).split(",")[0]}</span>
          <span className="text-lg font-bold text-stone-900">{new Date(bk.appointment_datetime).toLocaleString("en-GB", { day: "numeric", timeZone: "Africa/Lagos" })}</span>
          <span className="text-xs text-stone-400">{new Date(bk.appointment_datetime).toLocaleString("en-GB", { month: "short", timeZone: "Africa/Lagos" })}</span>
        </div>

        {/* Main info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span
              className="text-sm font-semibold text-stone-900 cursor-pointer hover:text-[#FF7A00]"
              onClick={() => navigate(`/admin/patients/${bk.patient_id}`)}
            >
              {bk.patient_name}
            </span>
            {statusBadge(bk.status)}
            <Badge variant="outline" className="text-xs capitalize">{bk.consultation_type?.replace(/_/g, " ")}</Badge>
            {bk.is_today && <Badge className="bg-[#FF7A00]/10 text-[#FF7A00] border-[#FF7A00]/20 text-xs">Today</Badge>}
            {nudge && <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs" data-testid={`unconfirmed-badge-${bk.booking_id}`}>Not confirmed</Badge>}
            {bk.attendance_confirmed_at && bk.status === "confirmed" && <Badge className="bg-green-50 text-green-700 border-green-200 text-xs" data-testid={`attendance-confirmed-${bk.booking_id}`}><CheckCircle2 className="w-3 h-3 mr-1" />Confirmed by patient</Badge>}
          </div>

          <div className="flex items-center gap-4 text-xs text-stone-500 flex-wrap">
            <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {formatDate(bk.appointment_datetime)} at {formatTime(bk.appointment_datetime)}</span>
            <span className="flex items-center gap-1">
              <ClipboardList className="w-3 h-3" />
              Intake: {bk.intake_status === "completed" ? (
                <span className="text-green-600 font-medium">Completed</span>
              ) : (
                <span className="text-amber-600 font-medium">Pending</span>
              )}
            </span>
            {bk.booking_source && bk.booking_source !== "manual" && (
              <Badge variant="outline" className="text-xs">via {bk.booking_source}</Badge>
            )}
          </div>
          {bk.intake_status !== "completed" && !bk.is_past && bk.patient_id && !["cancelled", "completed"].includes(bk.status) && (
            <div className="mt-2" data-testid={`intake-send-row-${bk.booking_id}`}>
              <SendIntakeForm patientId={bk.patient_id} token={token} email={bk.patient_email} phone={bk.patient_phone} lastSent={bk.intake_last_sent} compact />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {nudge && <NudgeButton item={nudge} token={token} onNudged={onNudged} />}
          {bk.meeting_url ? (
            <a
              href={bk.meeting_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 bg-[#FF7A00] text-white px-3 py-1.5 rounded-lg font-medium text-xs hover:bg-[#E56E00] transition-colors"
              data-testid={`join-meet-${bk.booking_id}`}
            >
              <Video className="w-3.5 h-3.5" /> Join Meeting
            </a>
          ) : (
            <Badge variant="outline" className="text-xs text-stone-400">No meeting link</Badge>
          )}
          <div className="relative">
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setShowActions(!showActions)} data-testid={`actions-${bk.booking_id}`}>
              <BarChart3 className="w-3.5 h-3.5 text-stone-400" />
            </Button>
            {showActions && (
              <div className="absolute right-0 top-8 bg-white border border-stone-200 rounded-lg shadow-lg py-1 z-10 min-w-[140px]">
                {bk.status === "confirmed" && (
                  <>
                    <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-stone-50 text-green-700" onClick={() => { onStatusUpdate(bk.booking_id, "completed"); setShowActions(false); }}>Mark completed</button>
                    <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-stone-50 text-red-600" onClick={() => { onStatusUpdate(bk.booking_id, "no_show"); setShowActions(false); }}>Mark no-show</button>
                    <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-stone-50 text-stone-500" onClick={() => { onStatusUpdate(bk.booking_id, "cancelled"); setShowActions(false); }}>Cancel</button>
                  </>
                )}
                <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-stone-50 text-stone-600" onClick={() => { navigate(`/admin/patients/${bk.patient_id}`); setShowActions(false); }}>View patient</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

const MiniStat = ({ label, value, icon: Icon, color = "text-stone-700", onClick, active, testid }) => (
  <Card data-testid={testid} className={`rounded-xl border-stone-200/60 p-3 text-center cursor-pointer hover:shadow-md hover:border-[#FF7A00]/30 transition-all ${active ? "ring-2 ring-[#FF7A00]/40 border-[#FF7A00]/30 bg-[#FFF8F2]" : ""}`} onClick={onClick}>
    <Icon className={`w-4 h-4 mx-auto mb-1 ${color}`} />
    <p className={`text-lg font-bold ${color}`}>{value || 0}</p>
    <p className="text-xs text-stone-400">{label}</p>
  </Card>
);

export default ConsultationsPage;
